import styled, { keyframes } from 'styled-components';
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import AnalyticsBreadcrumb from "ProtoType/NewAnalytics/AnalyticsBreadcrumb";
import QuestionContent from "./QuestionContent";
import Filters from '../Filters';
import Empty from "../components/EmptyFeedback";
import { filter_data } from "reports/Tag/calculations";
import Anchor from "../Anchor";
import QuestionDistributionChart from "./QuestionDistributionChart";
import Text from "new_components/atoms/Text";
import typography from "styles/typography";
import colors from "styles/colors";
import { useCustomDataRedirect } from "../common/useCustomDataRedirect";

const chartOptions = [
    { name: "Low", color: "#F15062" },
    { name: "Neutral", color: "#B3B3B3" },
    { name: "High", color: "#10B981" },
];

// Helper function to strip markdown and HTML tags from titles
const stripMarkdownAndHtml = (text) => {
    if (!text || typeof text !== 'string') return text;

    // Remove HTML tags (including <small> tags and their content)
    let cleaned = text.replace(/<small>[\s\S]*?<\/small>/gi, '');

    // Remove all other HTML tags
    cleaned = cleaned.replace(/<[^>]*>/g, '');

    // Remove markdown bold syntax (**text**)
    cleaned = cleaned.replace(/\*\*(.*?)\*\*/g, '$1');

    // Trim whitespace
    cleaned = cleaned.trim();

    return cleaned;
};





const QuestionAnalytics = ({
    core_data,
    outcomeQ,
    data,
    outcomeStandards,
    navigation,
    rule,
}) => {
    // Redirect protection for custom data from NewAnalytics
    useCustomDataRedirect();

    const [selected, setSelected] = useState(null);
    const [parentSelection, setParentSelection] = useState(null); // Track the parent factor/outcome when drilling into questions
    const [correctedData, setCorrectedData] = useState(null);
    const [isFading, setIsFading] = useState(false);
    const [isEmpty, setIsEmpty] = useState(false);
    const [sortDirection, setSortDirection] = useState('desc'); // 'desc' for highest to lowest, 'asc' for lowest to highest
    const [isSortTransitioning, setIsSortTransitioning] = useState(false);
    const [isAnchorTransitioning, setIsAnchorTransitioning] = useState(false);
    const [neutralBounds, setNeutralBounds] = useState({ min: 5, max: 6.99 });
    const [editingNeutral, setEditingNeutral] = useState(false);
    const [tempNeutralValues, setTempNeutralValues] = useState({ min: '', max: '' });
    const [calculationMethod, setCalculationMethod] = useState('averages'); // 'individual' or 'averages'
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [minCompletionThreshold, setMinCompletionThreshold] = useState(50); // Minimum % of questions a person must answer

    
    // Calculate all ranges based on neutral bounds
    const customRanges = {
        Low: { min: 0, max: Math.max(0, neutralBounds.min - 0.01) },
        Neutral: { min: neutralBounds.min, max: neutralBounds.max },
        High: { min: Math.min(10, neutralBounds.max + 0.01), max: 10 }
    };
    const [rangeUpdateTrigger, setRangeUpdateTrigger] = useState(0);

    const selectedFilters = useSelector(
        (state) => state.audit?.new_filters,
        shallowEqual
    );

    

    useEffect(() => {
        if (data.length === 0 && selectedFilters.length === 0) {
            return;
        }

        let filteredData = [];
        if (data?.length === 0) {
            setIsEmpty(true);
            return;
        }

        if (data[0][0]) {
            setIsEmpty(false);
            filteredData = filter_data(data[0], selectedFilters);
            return setCorrectedData(filteredData);
        }
        setIsEmpty(false);
        filteredData = filter_data(data, selectedFilters);
        setCorrectedData(filteredData);
    }, [data, selectedFilters]);

    // Force re-render when neutralBounds change
    useEffect(() => {
        // This will trigger a re-render of the component when ranges change
        // which will cause all calculations to re-run with new ranges
        console.log('Ranges updated, forcing recalculation:', customRanges);
    }, [neutralBounds]);

    // Watch for anchor changes when on question content
    useEffect(() => {
        if (selected && selectedFilters) {
            setIsAnchorTransitioning(true);
            const timer = setTimeout(() => {
                setIsAnchorTransitioning(false);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [selectedFilters, selected]);

    const handleSelect = (item, parent = null) => {
        setIsFading(true);
        setTimeout(() => {
            setSelected(item);
            // If selecting a question from within a factor/outcome, remember the parent
            if (parent) {
                setParentSelection(parent);
            } else if (item && item.questionCount === undefined) {
                // If clicking on a question but no parent provided, keep existing parent
                // This handles cases where we might be re-selecting
            } else {
                // Clicking on a factor/outcome at top level
                setParentSelection(null);
            }
            setIsFading(false);
        }, 300);
    };

    const handleBack = () => {
        setIsFading(true);
        setTimeout(() => {
            // If we have a parent (we're on a question), go back to the parent
            if (parentSelection) {
                setSelected(parentSelection);
                setParentSelection(null);
            } else {
            // Otherwise go back to overview
                setSelected(null);
            }
            setIsFading(false);
        }, 300);
    };

    const toggleSortDirection = () => {
        setIsSortTransitioning(true);
        setTimeout(() => {
            setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc');
            setTimeout(() => {
                setIsSortTransitioning(false);
            }, 50);
        }, 150);
    };

    const handleNeutralEdit = () => {
        setEditingNeutral(true);
        setTempNeutralValues({
            min: neutralBounds.min.toString(),
            max: neutralBounds.max.toString()
        });
    };

    const handleNeutralMinChange = (e) => {
        const value = e?.target?.value ?? '';
        setTempNeutralValues(prev => ({ ...prev, min: value }));
    };

    const handleNeutralMaxChange = (e) => {
        const value = e?.target?.value ?? '';
        setTempNeutralValues(prev => ({ ...prev, max: value }));
    };

    const handleNeutralKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleNeutralSave();
        } else if (e.key === 'Escape') {
            handleNeutralCancel();
        }
    };

    const handleNeutralSave = () => {
        const min = parseFloat(tempNeutralValues.min);
        const max = parseFloat(tempNeutralValues.max);
        
        if (isNaN(min) || isNaN(max) || min >= max || min < 0 || max > 10) {
            alert('Please enter valid range values (min must be less than max, and both between 0-10)');
            return;
        }
        
        setNeutralBounds({ min, max });
        
        // Force recalculation by updating trigger
        setRangeUpdateTrigger(prev => prev + 1);
        
        setEditingNeutral(false);
        setTempNeutralValues({ min: '', max: '' });
    };

    const handleNeutralCancel = () => {
        setEditingNeutral(false);
        setTempNeutralValues({ min: '', max: '' });
    };

    const computeDistributionData = (question) => {
        if (!correctedData) return null;
        
        // Debug: Log the current ranges being used
        console.log('Computing distribution with ranges:', customRanges);
        
        let responses;
        console.log("correctedData", correctedData);
        console.log("question", question);
        
        if (question.isOutcome) {
            // Extract responses for outcome questions
            responses = correctedData.flatMap(response => {
                if (!response.employee_outcomes || !response.employee_outcomes.responses) {
                    return [];
                }
                return response.employee_outcomes.responses.filter(r => 
                    r.q === question.q && r.s === question.s
                ).map(r => r.response);
            }).filter(val => {
                // Filter out null, undefined, 'N/A' responses and ensure it's a valid number
                return val !== null && 
                       val !== undefined && 
                       val !== 'N/A' && 
                       val !== '' &&
                       !isNaN(Number(val));
            }).map(val => Number(val)); // Convert to numbers to handle both int and float responses
        } else {
            // Extract responses for regular questions by matching dimension, factor, and question index
            responses = correctedData.flatMap(response =>
                response.questions?.filter(q => 
                    q.id === question.dimension && 
                    q.factor === question.factor && 
                    q.q === question.q
                ).map(q => q.response) || []
            ).filter(val => {
                // Filter out null, undefined, 'N/A' responses and ensure it's a valid number
                return val !== null && 
                       val !== undefined && 
                       val !== 'N/A' && 
                       val !== '' &&
                       !isNaN(Number(val));
            }).map(val => Number(val)); // Convert to numbers to handle both int and float responses
        }
        
        if (responses.length === 0) return null;
        
        // Check minimum response threshold based on Whitelabel rule
        const minimumResponses = core_data?.Whitelabel?.rule || 2;
        if (responses.length <= minimumResponses) return null;
        
        // Count responses in each category using exclusive upper bounds to avoid gaps
        const lowCount = responses.filter(score => score >= 0 && score < neutralBounds.min).length;
        const neutralCount = responses.filter(score => score >= neutralBounds.min && score < (neutralBounds.max + 0.01)).length;
        const highCount = responses.filter(score => score >= (neutralBounds.max + 0.01) && score <= 10).length;
        
        // Debug: Log the counts and ranges being used
        console.log(`Question: ${question.title || 'Unknown'}, Low: ${lowCount} (${customRanges.Low.min}-${customRanges.Low.max}), Neutral: ${neutralCount} (${customRanges.Neutral.min}-${customRanges.Neutral.max}), High: ${highCount} (${customRanges.High.min}-${customRanges.High.max})`);
        
        // Count responses outside the 0-10 range to handle edge cases
        const outsideRangeCount = responses.filter(score => score < 0 || score > 10).length;
        
        // Total valid responses that fall within our expected ranges
        const totalCategorizedResponses = lowCount + neutralCount + highCount;
        
        // If there are responses outside the 0-10 range, log them for debugging
        if (outsideRangeCount > 0) {
            const outsideRangeValues = responses.filter(score => score < 0 || score > 10);
            const questionId = question.isOutcome 
                ? `Outcome-${question.q}-${question.s}` 
                : `${question.dimension}-${question.factor}-${question.q}`;
            console.warn(`Question ${questionId} has responses outside 0-10 range:`, outsideRangeValues);
        }
        
        // Use total categorized responses for percentage calculation to ensure 100% total
        const totalForPercentage = totalCategorizedResponses;
        
        if (totalForPercentage === 0) return null;
        
        const distributionData = [
            { 
                name: "Low", 
                data: [(lowCount / totalForPercentage) * 100], 
                color: chartOptions[0].color, 
                count: lowCount 
            },
            { 
                name: "Neutral", 
                data: [(neutralCount / totalForPercentage) * 100], 
                color: chartOptions[1].color, 
                count: neutralCount 
            },
            { 
                name: "High", 
                data: [(highCount / totalForPercentage) * 100], 
                color: chartOptions[2].color, 
                count: highCount 
            }
        ];
        
        // Verify percentages add up to 100 (with small tolerance for floating point precision)
        const totalPercentage = distributionData.reduce((sum, item) => sum + item.data[0], 0);
        if (Math.abs(totalPercentage - 100) > 0.01) {
            const questionId = question.isOutcome 
                ? `Outcome-${question.q}-${question.s}` 
                : `${question.dimension}-${question.factor}-${question.q}`;
            console.warn(`Percentage total not 100% for question ${questionId}: ${totalPercentage}%`);
        }
        
        return distributionData;
    };

    const computeFactorDistributionData = (factorInfo) => {
        if (!correctedData) return null;
        
        // Collect scores based on calculation method
        const scores = [];
        
        if (factorInfo.isOutcome) {
            const outcomeCategory = core_data.outcomeQuestions.questions[factorInfo.outcomeCategory];
            if (!outcomeCategory) return null;

            if (calculationMethod === 'individual') {
                // Pool ALL question responses across the category (no per-person averaging)
                correctedData.forEach(response => {
                    if (!response.employee_outcomes || !response.employee_outcomes.responses) {
                        return;
                    }

                    const matches = response.employee_outcomes.responses
                        .filter(r => outcomeCategory.questions.some(q => q.q === r.q && q.s === r.s))
                        .map(r => r.response)
                        .filter(val => (
                            val !== null &&
                            val !== undefined &&
                            val !== 'N/A' &&
                            val !== '' &&
                            !isNaN(Number(val))
                        ))
                        .map(val => Number(val));

                    if (matches.length > 0) {
                        scores.push(...matches);
                    }
                });
            } else {
                // Calculate per-person averages
                correctedData.forEach(response => {
                    if (!response.employee_outcomes || !response.employee_outcomes.responses) {
                        return;
                    }

                    const personResponses = response.employee_outcomes.responses
                        .filter(r => outcomeCategory.questions.some(q => q.q === r.q && q.s === r.s))
                        .map(r => r.response)
                        .filter(val => (
                            val !== null &&
                            val !== undefined &&
                            val !== 'N/A' &&
                            val !== '' &&
                            !isNaN(Number(val))
                        ))
                        .map(val => Number(val));

                    const totalQuestions = outcomeCategory.questions.length;
                    const minRequired = Math.ceil(totalQuestions * (minCompletionThreshold / 100));
                    if (personResponses.length >= minRequired) {
                        const average = personResponses.reduce((sum, val) => sum + val, 0) / personResponses.length;
                        scores.push(average);
                    }
                });
            }
        } else {
            const dimension = core_data.questions.dimensions[factorInfo.dimension];
            const factor = dimension?.factors[factorInfo.factor];
            if (!factor) return null;

            if (calculationMethod === 'individual') {
                // Pool ALL question responses across the factor (no per-person averaging)
                correctedData.forEach(response => {
                    const personResponses = (response.questions || [])
                        .filter(q => 
                            q.id === factorInfo.dimension && 
                            q.factor === factorInfo.factor
                        )
                        .map(q => q.response)
                        .filter(val => {
                            return val !== null && 
                                   val !== undefined && 
                                   val !== 'N/A' && 
                                   val !== '' &&
                                   !isNaN(Number(val));
                        })
                        .map(val => Number(val));
                    
                    if (personResponses.length > 0) {
                        scores.push(...personResponses);
                    }
                });
            } else {
                // Calculate per-person averages
                correctedData.forEach(response => {
                    const personResponses = (response.questions || [])
                        .filter(q => 
                            q.id === factorInfo.dimension && 
                            q.factor === factorInfo.factor
                        )
                        .map(q => q.response)
                        .filter(val => {
                            return val !== null && 
                                   val !== undefined && 
                                   val !== 'N/A' && 
                                   val !== '' &&
                                   !isNaN(Number(val));
                        })
                        .map(val => Number(val));
                    
                    const totalQuestions = factor.questions.length;
                    const minRequired = Math.ceil(totalQuestions * (minCompletionThreshold / 100));
                    if (personResponses.length >= minRequired) {
                        const average = personResponses.reduce((sum, val) => sum + val, 0) / personResponses.length;
                        scores.push(average);
                    }
                });
            }
        }
        
        if (scores.length === 0) {
            return null;
        }
        
        // Check minimum response threshold
        const minimumResponses = core_data?.Whitelabel?.rule || 2;
        if (scores.length <= minimumResponses) return null;
        
        // Count scores in each category using exclusive upper bounds to avoid gaps
        const lowCount = scores.filter(score => score >= 0 && score < neutralBounds.min).length;
        const neutralCount = scores.filter(score => score >= neutralBounds.min && score < (neutralBounds.max + 0.01)).length;
        const highCount = scores.filter(score => score >= (neutralBounds.max + 0.01) && score <= 10).length;
        
        const totalCategorizedResponses = lowCount + neutralCount + highCount;
        if (totalCategorizedResponses === 0) return null;
        
        const distributionData = [
            { 
                name: "Low", 
                data: [(lowCount / totalCategorizedResponses) * 100], 
                color: chartOptions[0].color, 
                count: lowCount 
            },
            { 
                name: "Neutral", 
                data: [(neutralCount / totalCategorizedResponses) * 100], 
                color: chartOptions[1].color, 
                count: neutralCount 
            },
            { 
                name: "High", 
                data: [(highCount / totalCategorizedResponses) * 100], 
                color: chartOptions[2].color, 
                count: highCount 
            }
        ];
        
        return distributionData;
    };

    if (!core_data) {
        return <div>Loading...</div>;
    }


    // Get all questions from all factors
    const allQuestions = core_data.questions.dimensions.reduce((acc, dimension, i) => {
        const questions = dimension.factors.reduce((factorAcc, factor, fIndex) => {
            // Update the object structure to look like this:
            // {
            //     "q": 0, // question index within the factor.questions array
            //     "dimension": i, // index of the dimension in core_data.questions.dimensions
            //     "factor": fIndex, // index of the factor in the dimension.factors array
            //     "title": "Question 1"
            // }
            return [...factorAcc, ...factor.questions.map((q, qIndex) => ({
                q: qIndex,
                dimension: i,
                factor: fIndex,
                title: q.q,
                isOutcome: false
            }))];
        }, []);
        return [...acc, ...questions];
    }, []);

    // Add outcome questions if they exist
    if (core_data.outcomeQuestions && core_data.outcomeQuestions.questions) {
        const outcomeQuestions = core_data.outcomeQuestions.questions.reduce((acc, category, categoryIndex) => {
            const questions = category.questions.map((q) => ({
                q: q.q,
                s: q.s,
                outcomeCategory: categoryIndex,
                categoryName: category.name,
                title: q.question,
                isOutcome: true
            }));
            return [...acc, ...questions];
        }, []);
        allQuestions.push(...outcomeQuestions);
    }

    // Create factor data structure for factors view
    const allFactors = core_data.questions.dimensions.reduce((acc, dimension, dimensionIndex) => {
        const factors = dimension.factors.map((factor, factorIndex) => ({
            dimension: dimensionIndex,
            factor: factorIndex,
            dimensionName: dimension.title || dimension.name,
            title: factor.title,
            isOutcome: false,
            questionCount: factor.questions.length
        }));
        return [...acc, ...factors];
    }, []);

    // Add outcome categories as factors
    if (core_data.outcomeQuestions && core_data.outcomeQuestions.questions) {
        const outcomeFactors = core_data.outcomeQuestions.questions.map((category, categoryIndex) => ({
            outcomeCategory: categoryIndex,
            categoryName: category.name,
            title: category.name,
            isOutcome: true,
            questionCount: category.questions.length
        }));
        allFactors.push(...outcomeFactors);
    }

    // Separate outcome categories from regular factors for different views
    const outcomeCategories = allFactors.filter(f => f.isOutcome);
    const regularFactors = allFactors.filter(f => !f.isOutcome);


    const ScoreLegend = () => (
        <LegendContainer>
            <LegendTitle>
                <Text typographyStyle={typography.caption.semibold} color={colors.text.dark}>
                    Score Range:
                </Text>
            </LegendTitle>
            <LegendItems>
                {chartOptions.map((option) => (
                    <LegendItem key={option.name}>
                        <LegendColorBox color={option.color} />
                        {editingNeutral && option.name === 'Neutral' ? (
                            <RangeEditContainer>
                                <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                    {option.name} (
                                </Text>
                                <RangeInput
                                    key="neutral-min"
                                    type="number"
                                    step="0.01"
                                    value={tempNeutralValues.min}
                                    onChange={handleNeutralMinChange}
                                    onKeyDown={handleNeutralKeyDown}
                                    placeholder="min"
                                    autoFocus
                                />
                                <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                    -
                                </Text>
                                <RangeInput
                                    key="neutral-max"
                                    type="number"
                                    step="0.01"
                                    value={tempNeutralValues.max}
                                    onChange={handleNeutralMaxChange}
                                    onKeyDown={handleNeutralKeyDown}
                                    placeholder="max"
                                />
                                <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                    )
                                </Text>
                                <RangeButton onClick={handleNeutralSave}>✓</RangeButton>
                                <RangeButton onClick={handleNeutralCancel}>✕</RangeButton>
                            </RangeEditContainer>
                        ) : (
                            <ClickableRangeText 
                                onClick={option.name === 'Neutral' ? handleNeutralEdit : undefined}
                                isClickable={option.name === 'Neutral'}
                            >
                                <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                    {option.name} ({Math.round(customRanges[option.name].min * 100) / 100}-{Math.round(customRanges[option.name].max * 100) / 100})
                                    {option.name === 'Neutral' && (
                                        <span style={{ fontSize: '10px', color: '#999', marginLeft: '4px' }}>
                                            (click to edit)
                                        </span>
                                    )}
                                </Text>
                            </ClickableRangeText>
                        )}
                    </LegendItem>
                ))}
            </LegendItems>
        </LegendContainer>
    );

    return (
        <Container>
            <AnalyticsBreadcrumb
                currentComponent="question"
                selectedItem={selected}
                parentItem={parentSelection}
                onBackToOverview={handleBack}
                onBackToParent={() => {
                    if (parentSelection) {
                        handleBack();
                    }
                }}
            />
            {/* <BlueTitle>Culture Analytics</BlueTitle> */}
            <Title>
                <TitleIconWrapper aria-hidden>
                    <QuestionAnalysisIcon />
                </TitleIconWrapper>
                {selected ? stripMarkdownAndHtml(selected.title) : 'Question Distributions'}
            </Title>

            <SettingsRow>
                <SettingsButton onClick={() => setShowSettingsModal(true)}>
                    <SettingsIcon />
                    <span>Settings</span>
                </SettingsButton>
            </SettingsRow>

            {showSettingsModal && createPortal(
                <ModalOverlay onClick={() => setShowSettingsModal(false)}>
                    <SettingsModal onClick={(e) => e.stopPropagation()}>
                        <ModalHeader>
                            <ModalTitle>Distribution Settings</ModalTitle>
                            <CloseButton onClick={() => setShowSettingsModal(false)}>×</CloseButton>
                        </ModalHeader>
                        
                        <ModalContent>
                            <SettingsSection>
                                <SettingLabel>Calculation Method</SettingLabel>
                                <SettingDescription>
                                    Choose how scores are aggregated for factor-level distributions.
                                </SettingDescription>
                                
                                <MethodOptionContainer>
                                    <MethodOption 
                                        isSelected={calculationMethod === 'individual'}
                                        onClick={() => setCalculationMethod('individual')}
                                    >
                                        <MethodOptionHeader>
                                            <RadioCircle isSelected={calculationMethod === 'individual'} />
                                            <MethodOptionTitle>Individual Scores</MethodOptionTitle>
                                        </MethodOptionHeader>
                                        <MethodOptionDescription>
                                            Each question response is counted as a separate data point. People who answered more questions have proportionally more influence on the distribution.
                                        </MethodOptionDescription>
                                    </MethodOption>
                                    
                                    <MethodOption 
                                        isSelected={calculationMethod === 'averages'}
                                        onClick={() => setCalculationMethod('averages')}
                                    >
                                        <MethodOptionHeader>
                                            <RadioCircle isSelected={calculationMethod === 'averages'} />
                                            <MethodOptionTitle>Averages</MethodOptionTitle>
                                        </MethodOptionHeader>
                                        <MethodOptionDescription>
                                            Each person's responses are averaged together first, then that single average is used. This gives each person equal weight regardless of how many questions they answered.
                                        </MethodOptionDescription>
                                    </MethodOption>
                                </MethodOptionContainer>
                            </SettingsSection>
                            
                            {calculationMethod === 'averages' && (
                                <>
                                    <SettingsDivider />
                                    
                                    <SettingsSection>
                                        <SettingLabel>Minimum Category Completion</SettingLabel>
                                        <SettingDescription>
                                            Only include a person's average if they answered at least this percentage of questions in the category or outcome category. This prevents partial responses from skewing the distribution.
                                        </SettingDescription>
                                        
                                        <ThresholdInputContainer>
                                            <ThresholdInput
                                                type="number"
                                                min="0"
                                                max="100"
                                                value={minCompletionThreshold}
                                                onChange={(e) => {
                                                    const val = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                                                    setMinCompletionThreshold(val);
                                                }}
                                            />
                                            <ThresholdUnit>%</ThresholdUnit>
                                        </ThresholdInputContainer>
                                        
                                        <ThresholdHint>
                                            {minCompletionThreshold === 0 
                                                ? 'All responses will be included (no minimum threshold).'
                                                : `People must answer at least ${minCompletionThreshold}% of questions to be included.`
                                            }
                                        </ThresholdHint>
                                    </SettingsSection>
                                </>
                            )}
                        </ModalContent>
                        
                        <ModalFooter>
                            <DoneButton onClick={() => setShowSettingsModal(false)}>
                                Done
                            </DoneButton>
                        </ModalFooter>
                    </SettingsModal>
                </ModalOverlay>,
                document.body
            )}

            <OptionsContainer>
                <SortContainer>
                    <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                        Sort by:
                    </Text>
                    <SortButton onClick={toggleSortDirection}>
                        <Text typographyStyle={typography.caption.semibold} color={colors.text.dark}>
                            {sortDirection === 'desc' ? 'High to Low' : 'Low to High'} ↕
                        </Text>
                    </SortButton>
                </SortContainer>
                <ScoreLegend />
            </OptionsContainer>

            <FiltersRow>
                <Filters />
                {selected !== null && selected.questionCount === undefined && <Anchor anchorLabel=" " />}
            </FiltersRow>

            <Content>
                {selected === null || (selected !== null && selected.questionCount !== undefined) ? (
                    <OverviewContainer style={{ 
                        opacity: isFading || isSortTransitioning ? 0.3 : 1, 
                        transition: 'opacity 0.3s ease-in-out',
                        pointerEvents: isSortTransitioning ? 'none' : 'auto'
                    }}>
                        {(() => {
                            // Show questions list when drilling into an outcome category or factor
                            if (selected !== null && selected.isOutcome && selected.questionCount !== undefined) {
                                // Get questions for the selected outcome category
                                const categoryQuestions = allQuestions.filter(q =>
                                    q.isOutcome && q.outcomeCategory === selected.outcomeCategory
                                );

                                const questionsWithData = categoryQuestions.map(question => {
                                    const dist = computeDistributionData(question);
                                    if (!dist) return null;
                                    const questionKey = `outcome-${question.q}-${question.s}`;
                                    return {
                                        question,
                                        dist,
                                        questionKey
                                    };
                                }).filter(Boolean);

                                if (questionsWithData.length === 0) {
                                    const minimumResponses = core_data?.Whitelabel?.rule || 2;
                                    return (
                                        <EmptyStateContainer>
                                            <Empty
                                                title="Insufficient Response Data"
                                                message={`Questions require more than ${minimumResponses} responses to display analytics. Please check back when more survey data is available.`}
                                            />
                                        </EmptyStateContainer>
                                    );
                                }

                                return (
                                    <>
                                        {questionsWithData.sort((a, b) => {
                                            const aHigh = a.dist?.find(entry => entry.name === "High")?.data[0] || 0;
                                            const bHigh = b.dist?.find(entry => entry.name === "High")?.data[0] || 0;
                                            return sortDirection === 'desc' ? bHigh - aHigh : aHigh - bHigh;
                                        }).map(({ question, dist, questionKey }) => (
                                            <OverviewItem key={questionKey} onClick={() => handleSelect(question, selected)}>
                                                <QuestionLabel><Text typographyStyle={typography.caption.regular} color={colors.text.dark}>{stripMarkdownAndHtml(question.title)}</Text></QuestionLabel>
                                                <ChartWrapper><QuestionDistributionChart data={dist} /></ChartWrapper>
                                            </OverviewItem>
                                        ))}
                                    </>
                                );
                            }

                            // Show questions list when drilling into a regular factor (driver)
                            if (selected !== null && !selected.isOutcome && selected.questionCount !== undefined) {
                                // Get questions for the selected factor
                                const factorQuestions = allQuestions.filter(q =>
                                    !q.isOutcome &&
                                    q.dimension === selected.dimension &&
                                    q.factor === selected.factor
                                );

                                const questionsWithData = factorQuestions.map(question => {
                                    const dist = computeDistributionData(question);
                                    if (!dist) return null;
                                    const questionKey = `${question.dimension}-${question.factor}-${question.q}`;
                                    return {
                                        question,
                                        dist,
                                        questionKey
                                    };
                                }).filter(Boolean);

                                if (questionsWithData.length === 0) {
                                    const minimumResponses = core_data?.Whitelabel?.rule || 2;
                                    return (
                                        <EmptyStateContainer>
                                            <Empty 
                                                title="Insufficient Response Data"
                                                message={`Questions require more than ${minimumResponses} responses to display analytics. Please check back when more survey data is available.`}
                                            />
                                        </EmptyStateContainer>
                                    );
                                }

                                return (
                                    <>
                                        {questionsWithData.sort((a, b) => {
                                            const aHigh = a.dist?.find(entry => entry.name === "High")?.data[0] || 0;
                                            const bHigh = b.dist?.find(entry => entry.name === "High")?.data[0] || 0;
                                            return sortDirection === 'desc' ? bHigh - aHigh : aHigh - bHigh;
                                        }).map(({ question, dist, questionKey }) => (
                                            <OverviewItem key={questionKey} onClick={() => handleSelect(question, selected)}>
                                                <QuestionLabel><Text typographyStyle={typography.caption.regular} color={colors.text.dark}>{stripMarkdownAndHtml(question.title)}</Text></QuestionLabel>
                                                <ChartWrapper><QuestionDistributionChart data={dist} /></ChartWrapper>
                                            </OverviewItem>
                                        ))}
                                    </>
                                );
                            }

                            // Combined view: show both outcome categories and driver factors together
                            const combinedFactors = [...allFactors].map(factor => {
                                const dist = computeFactorDistributionData(factor);
                                if (!dist) return null;
                                const factorKey = factor.isOutcome
                                    ? `outcome-factor-${factor.outcomeCategory}`
                                    : `factor-${factor.dimension}-${factor.factor}`;
                                return {
                                    factor,
                                    dist,
                                    factorKey
                                };
                            }).filter(Boolean);

                            if (combinedFactors.length === 0) {
                                const minimumResponses = core_data?.Whitelabel?.rule || 2;
                                return (
                                    <EmptyStateContainer>
                                        <Empty
                                            title="Insufficient Response Data"
                                            message={`More than ${minimumResponses} responses are required to display analytics. Please check back when more survey data is available.`}
                                        />
                                    </EmptyStateContainer>
                                );
                            }

                            return combinedFactors.sort((a, b) => {
                                const aHigh = a.dist?.find(entry => entry.name === "High")?.data[0] || 0;
                                const bHigh = b.dist?.find(entry => entry.name === "High")?.data[0] || 0;
                                return sortDirection === 'desc' ? bHigh - aHigh : aHigh - bHigh;
                            }).map(({ factor, dist, factorKey }) => (
                                <OverviewItem key={factorKey} onClick={() => handleSelect(factor)}>
                                    <QuestionLabel>
                                        <div>
                                            <Text typographyStyle={typography.caption.regular} color={colors.text.dark}>
                                                {stripMarkdownAndHtml(factor.title)}
                                            </Text>
                                            <div style={{ marginTop: '2px' }}>
                                                <Text typographyStyle={typography.caption.light} color={colors.text.light} style={{ fontSize: '11px' }}>
                                                    {factor.questionCount} question{factor.questionCount !== 1 ? 's' : ''}
                                                </Text>
                                            </div>
                                        </div>
                                    </QuestionLabel>
                                    <ChartWrapper><QuestionDistributionChart data={dist} /></ChartWrapper>
                                </OverviewItem>
                            ));
                        })()}
                    </OverviewContainer>
                ) : (
                    <DetailContainer style={{ 
                        opacity: isFading ? 0 : 1, 
                        transition: 'opacity 0.3s ease-in-out' 
                        }}>
                        <QuestionContentWrapper style={{
                            opacity: isAnchorTransitioning ? 0.3 : 1,
                            transition: 'opacity 0.3s ease-in-out'
                        }}>
                            <QuestionContent
                                question={selected}
                                core_data={core_data}
                                outcomeQ={outcomeQ}
                                data={correctedData}
                                selectedQuestion={selected}
                                outcomeStandards={outcomeStandards}
                                navigation={navigation}
                                    rule={rule}
                                isFactor={selected && (selected.questionCount !== undefined)}
                                customRanges={customRanges}
                                sortDirection={sortDirection}
                                calculationMethod={calculationMethod}
                                minCompletionThreshold={minCompletionThreshold}
                            />
                        </QuestionContentWrapper>
                    </DetailContainer>
                )}
            </Content>
        </Container>
    );
};

export default QuestionAnalytics;

const QuestionAnalysisIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M20 20V13M12 20V10M4 20L4 16M13.4067 5.0275L18.5751 6.96567M10.7988 5.40092L5.20023 9.59983M21.0607 6.43934C21.6464 7.02513 21.6464 7.97487 21.0607 8.56066C20.4749 9.14645 19.5251 9.14645 18.9393 8.56066C18.3536 7.97487 18.3536 7.02513 18.9393 6.43934C19.5251 5.85355 20.4749 5.85355 21.0607 6.43934ZM5.06066 9.43934C5.64645 10.0251 5.64645 10.9749 5.06066 11.5607C4.47487 12.1464 3.52513 12.1464 2.93934 11.5607C2.35355 10.9749 2.35355 10.0251 2.93934 9.43934C3.52513 8.85355 4.47487 8.85355 5.06066 9.43934ZM13.0607 3.43934C13.6464 4.02513 13.6464 4.97487 13.0607 5.56066C12.4749 6.14645 11.5251 6.14645 10.9393 5.56066C10.3536 4.97487 10.3536 4.02513 10.9393 3.43934C11.5251 2.85355 12.4749 2.85355 13.0607 3.43934Z"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    font-family: 'Raleway';
`;

const BlueTitle = styled.div`
    color: #2D70E2;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 15px;
    margin-top: -20px;
`;

const Title = styled.div`
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 30px;
    color: #2A3039;
    display: flex;
    align-items: center;
    gap: 10px;
    line-height: 1.3;
`;

const TitleIconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    background: #F8F9FA;
    border-radius: 7px;
    color: #1F2937;
`;

const Content = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    margin-top: 0px;
`;

const OptionsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    min-height: 40px;
    gap: 24px;
    margin-bottom: 8px;
`;

const FiltersRow = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 12px;
`;

const SortContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 8px;
`;

const SortButton = styled.button`
    background: none;
    border: none;
    padding: 4px 8px;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #f0f0f0;
    }
`;

const LegendContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    background-color: #f9fafb;
    border-radius: 6px;
    border: 1px solid #e1e5e9;
`;

const LegendTitle = styled.div`
    flex-shrink: 0;
`;

const LegendItems = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
`;

const LegendColorBox = styled.div`
    width: 10px;
    height: 10px;
    background-color: ${props => props.color};
    border-radius: 2px;
    flex-shrink: 0;
`;

const ChartWrapper = styled.div`
    flex: 1;
    min-width: 300px;
`;

const QuestionLabel = styled.div`
    flex: 0 0 300px;
    white-space: normal;
    word-break: break-word;
`;

const OverviewContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const OverviewItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;
    padding: 14px 16px;
    min-height: 50px;
    cursor: pointer;
    &:hover { background-color: #f5f5f5; }
`;

const DetailContainer = styled.div`
    width: 100%;
    padding: 20px;
`;

const QuestionContentWrapper = styled.div`
    width: 100%;
    height: 100%;
`;

const EmptyStateContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 400px;
    padding: 40px;
`;

const SettingsRow = styled.div`
    display: flex;
    justify-content: flex-end;
    width: 100%;
    margin-bottom: 12px;
`;

const SettingsButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: 1px solid #e1e5e9;
    border-radius: 4px;
    padding: 6px 12px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    color: #4B5563;
    font-family: 'Raleway', sans-serif;
    transition: all 0.15s ease;
    
    &:hover {
        background-color: #f9fafb;
        border-color: #d1d5db;
    }
`;

const SettingsIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
);

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    backdrop-filter: blur(2px);
`;

const SettingsModal = styled.div`
    background: white;
    border-radius: 8px;
    width: 100%;
    max-width: 480px;
    max-height: 90vh;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
    animation: modalSlideIn 0.2s ease-out;
    
    @keyframes modalSlideIn {
        from {
            opacity: 0;
            transform: translateY(-10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
`;

const ModalHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h3`
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #1f2937;
    font-family: 'Raleway', sans-serif;
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    color: #9ca3af;
    cursor: pointer;
    padding: 0;
    line-height: 1;
    transition: color 0.15s ease;
    
    &:hover {
        color: #4b5563;
    }
`;

const ModalContent = styled.div`
    padding: 20px;
    overflow-y: auto;
    max-height: calc(90vh - 130px);
`;

const SettingsSection = styled.div`
    margin-bottom: 0;
`;

const SettingLabel = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
    font-family: 'Raleway', sans-serif;
`;

const SettingDescription = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin: 0 0 16px 0;
    line-height: 1.5;
    font-family: 'Raleway', sans-serif;
`;

const SettingsDivider = styled.div`
    height: 1px;
    background: #e5e7eb;
    margin: 24px 0;
`;

const MethodOptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const MethodOption = styled.div`
    border: 1px solid ${props => props.isSelected ? '#2D70E2' : '#e5e7eb'};
    border-radius: 6px;
    padding: 14px;
    cursor: pointer;
    background: ${props => props.isSelected ? 'rgba(45, 112, 226, 0.03)' : 'transparent'};
    transition: all 0.15s ease;
    
    &:hover {
        border-color: ${props => props.isSelected ? '#2D70E2' : '#d1d5db'};
        background: ${props => props.isSelected ? 'rgba(45, 112, 226, 0.03)' : '#fafafa'};
    }
`;

const MethodOptionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
`;

const RadioCircle = styled.div`
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid ${props => props.isSelected ? '#2D70E2' : '#d1d5db'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    
    &::after {
        content: '';
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: ${props => props.isSelected ? '#2D70E2' : 'transparent'};
        transition: background 0.15s ease;
    }
`;

const MethodOptionTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    font-family: 'Raleway', sans-serif;
`;

const MethodOptionDescription = styled.div`
    font-size: 12px;
    color: #6b7280;
    line-height: 1.5;
    padding-left: 26px;
    font-family: 'Raleway', sans-serif;
`;

const ThresholdInputContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
`;

const ThresholdInput = styled.input`
    width: 70px;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
    text-align: center;
    font-family: 'Raleway', sans-serif;
    transition: all 0.15s ease;
    
    &:focus {
        outline: none;
        border-color: #2D70E2;
        box-shadow: 0 0 0 3px rgba(45, 112, 226, 0.1);
    }
    
    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
`;

const ThresholdUnit = styled.span`
    font-size: 14px;
    font-weight: 500;
    color: #6b7280;
    font-family: 'Raleway', sans-serif;
`;

const ThresholdHint = styled.div`
    font-size: 12px;
    color: #9ca3af;
    font-style: italic;
    font-family: 'Raleway', sans-serif;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 16px 20px;
    border-top: 1px solid #e5e7eb;
`;

const DoneButton = styled.button`
    background: #2D70E2;
    color: white;
    border: none;
    border-radius: 6px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: 'Raleway', sans-serif;
    transition: all 0.15s ease;
    
    &:hover {
        background: #2563eb;
    }
`;

const ClickableRangeText = styled.div`
    cursor: ${props => props.isClickable ? 'pointer' : 'default'};
    padding: 2px 4px;
    border-radius: 3px;
    transition: background-color 0.2s ease;
    
    &:hover {
        background-color: ${props => props.isClickable ? '#f0f0f0' : 'transparent'};
    }
`;

const RangeEditContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 2px 4px;
`;

const RangeInput = styled.input`
    width: 50px;
    padding: 2px 4px;
    border: 1px solid #ccc;
    border-radius: 3px;
    font-size: 11px;
    text-align: center;
    background-color: white;
    
    &:focus {
        outline: none;
        border-color: #2D70E2;
        box-shadow: 0 0 0 1px rgba(45, 112, 226, 0.1);
    }
    
    &::placeholder {
        color: #999;
        opacity: 0.7;
    }
`;

const RangeButton = styled.button`
    background: none;
    border: none;
    padding: 2px 4px;
    cursor: pointer;
    font-size: 12px;
    color: #666;
    border-radius: 3px;
    transition: all 0.2s ease;
    
    &:hover {
        background-color: #f0f0f0;
        color: #333;
    }
    
    &:first-of-type {
        color: #28a745;
        
        &:hover {
            color: #1e7e34;
        }
    }
    
    &:last-of-type {
        color: #dc3545;
        
        &:hover {
            color: #c82333;
        }
    }
`;
